import {
  Box,
  Button,
  Chip,
  IconButton,
  Menu,
  MenuItem,
  Stack,
  Typography,
  Paper,
  Divider,
  Tooltip,
} from '@mui/material';
import { alpha } from '@mui/material/styles';
import { useEffect, useState } from 'react';
import { getReportsV2 } from '../../../../helpers/api';
import { openNotification } from '../../../../helpers/notification';
import LoaderSkeleton from '../../../common/LoaderSkeleton';
import { Empty } from 'antd';
import MUIDataTable from 'mui-datatables';
import { reportStatuses, reportTypes } from '../../../../helpers/appstate';
import { Link } from 'react-router-dom';
import {
  KeyboardArrowRight,
  PieChartOutline,
  HistoryOutlined,
  CheckCircleOutline,
  HighlightOff,
} from '@mui/icons-material';
import AppPagination from '../../../common/AppPagination';
import ResultLock from '../result-lock/ResultLock';
import GenerateCertificate from '../../facilities2/facility-quarters/GenerateCertificate';
import SendCertificate from '../../facilities2/facility-quarters/SendCertificate';
import PublishResult from '../../facilities2/facility-quarters/PublishResult';
import ApproveResult from '../result-action/ApproveResult';
import RejectResult from '../result-action/RejectResult';

export default function SubmittedResult({ props, history }) {
  const { userPermission } = props;
  const userPerm = userPermission?.map((mp) => mp.trim().toLowerCase()) || [];
  const canPublishResult = userPerm.includes('publish_report');

  const pageSize = 10;
  const [pageNum, setPageNum] = useState(1);
  const [total, setTotal] = useState(0);
  const [resultList, setResultList] = useState([]);
  const [resultLoading, setResultLoading] = useState(false);
  const [resultSelected, setResultSelected] = useState();
  const [openResultLocker, setOpenResultLocker] = useState(false);
  const [openGenCert, setOpenGenCert] = useState(false);
  const [openSendCert, setOpenSendCert] = useState(false);
  const [refreshPage, setRefreshPage] = useState(false);
  const [openPublish, setOpenPublish] = useState(false);
  const [openApproveResult, setOpenApproveResult] = useState(false);
  const [openRejectResult, setOpenRejectResult] = useState(false);

  useEffect(() => {
    fetchResultList();
  }, [pageNum]);

  useEffect(() => {
    if (refreshPage) fetchResultList();
  }, [refreshPage]);

  const fetchResultList = async () => {
    setResultLoading(true);
    try {
      const res = await getReportsV2(
        '',
        '',
        '',
        reportStatuses.SUBMITTED,
        '',
        '',
        '',
        '',
        pageNum,
        pageSize
      );
      const { data, status, totalCount } = res?.result;
      if (status?.code === '00') {
        setResultList(data);
        setTotal(totalCount);
      } else {
        openNotification({ type: 'error', message: status?.desc });
      }
    } catch (error) {
      openNotification({ type: 'error', message: 'Error fetching submitted results' });
    } finally {
      setResultLoading(false);
      setRefreshPage(false);
    }
  };

  const renderContent = () => {
    const columns = [
      {
        name: 'reportTitle',
        label: 'Submission Details',
        options: {
          customHeadLabelRender: (a) => (
            <Typography
              variant="button"
              fontWeight={700}
              color="text.secondary"
              sx={{ fontSize: '0.75rem' }}
            >
              {a.label}
            </Typography>
          ),
          customBodyRenderLite: (index) => {
            const item = resultList[index];
            return (
              <Stack direction="row" spacing={2} alignItems="center" sx={{ py: 1 }}>
                <Box
                  sx={{
                    p: 1,
                    borderRadius: '10px',
                    bgcolor: alpha('#1976d2', 0.1),
                    color: 'primary.main',
                    display: 'flex',
                  }}
                >
                  <PieChartOutline sx={{ fontSize: 18 }} />
                </Box>
                <Box>
                  <Typography variant="body2" fontWeight={700} color="text.primary">
                    {item.reportTitle}
                  </Typography>
                  <Typography
                    variant="caption"
                    color="textSecondary"
                    sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}
                  >
                    <HistoryOutlined sx={{ fontSize: 12 }} />
                    Submitted:{' '}
                    {new Date(item.entryDate).toLocaleDateString('en-NG', {
                      year: 'numeric',
                      month: 'short',
                      day: 'numeric',
                    })}
                  </Typography>
                </Box>
              </Stack>
            );
          },
        },
      },
      {
        name: 'facility',
        label: 'Facility',
        options: {
          customHeadLabelRender: (a) => (
            <Typography
              variant="button"
              fontWeight={700}
              color="text.secondary"
              sx={{ fontSize: '0.75rem' }}
            >
              {a.label}
            </Typography>
          ),
          customBodyRenderLite: (index) => (
            <Box>
              <Link
                to={`/facilities/${resultList[index].facilityId}/view`}
                style={{ textDecoration: 'none' }}
              >
                <Typography
                  variant="body2"
                  fontWeight={600}
                  color="primary"
                  sx={{ '&:hover': { textDecoration: 'underline' } }}
                >
                  {resultList[index].facility}
                </Typography>
              </Link>
              <Typography
                variant="caption"
                color="textSecondary"
                sx={{ textTransform: 'capitalize' }}
              >
                {resultList[index].stateName?.toLowerCase()} • {resultList[index].sectorName}
              </Typography>
            </Box>
          ),
        },
      },
      {
        name: 'reportStatus',
        label: 'Status',
        options: {
          customHeadLabelRender: (a) => (
            <Typography
              variant="button"
              fontWeight={700}
              color="text.secondary"
              sx={{ fontSize: '0.75rem' }}
            >
              {a.label}
            </Typography>
          ),
          customBodyRenderLite: (index) => (
            <Chip
              label="Awaiting Approval"
              size="small"
              sx={{
                fontWeight: 700,
                fontSize: '0.7rem',
                bgcolor: alpha('#1976d2', 0.1),
                color: '#1976d2',
                borderRadius: '6px',
              }}
            />
          ),
        },
      },
      {
        name: '',
        label: 'Action',
        options: {
          customHeadLabelRender: (a) => (
            <Typography
              variant="button"
              fontWeight={700}
              color="text.secondary"
              sx={{ fontSize: '0.75rem' }}
              align="center"
            >
              {a.label}
            </Typography>
          ),
          customBodyRenderLite: (index) => {
            const i = resultList[index];
            return (
              <Stack direction="row" spacing={1} justifyContent="center">
                {canPublishResult && (
                  <>
                    <Tooltip title="Approve Submission">
                      <IconButton
                        size="small"
                        color="success"
                        onClick={() => {
                          setResultSelected(i);
                          setOpenApproveResult(true);
                        }}
                        sx={{ bgcolor: alpha('#2e7d32', 0.1) }}
                      >
                        <CheckCircleOutline fontSize="small" />
                      </IconButton>
                    </Tooltip>
                    <Tooltip title="Reject Submission">
                      <IconButton
                        size="small"
                        color="error"
                        onClick={() => {
                          setResultSelected(i);
                          setOpenRejectResult(true);
                        }}
                        sx={{ bgcolor: alpha('#d32f2f', 0.1) }}
                      >
                        <HighlightOff fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  </>
                )}
                <Button
                  variant="outlined"
                  size="small"
                  endIcon={<KeyboardArrowRight />}
                  sx={{ textTransform: 'none', fontWeight: 700, borderRadius: '8px' }}
                  onClick={() =>
                    history.push(
                      `results/view/${i?.facilityId}/${i?.reportId}/${i?.reportQuarter}/${i?.reportYear}`
                    )
                  }
                >
                  Preview
                </Button>
              </Stack>
            );
          },
        },
      },
    ];

    return (
      <MUIDataTable
        data={resultList}
        columns={columns}
        options={{
          elevation: 0,
          search: false,
          filter: false,
          download: false,
          print: false,
          viewColumns: false,
          selectableRows: 'none',
          responsive: 'standard',
          pagination: false,
          setRowProps: (ev, dataIndex) => ({
            style: { backgroundColor: dataIndex % 2 === 0 ? '#ffffff' : alpha('#f5f7ff', 0.5) },
          }),
          setTableProps: () => ({ size: 'small' }),
        }}
      />
    );
  };

  return (
    <Box>
      <Paper
        elevation={0}
        sx={{
          borderRadius: '16px',
          border: '1px solid',
          borderColor: 'divider',
          overflow: 'hidden',
        }}
      >
        {resultLoading ? (
          <Box sx={{ p: 4 }}>
            <LoaderSkeleton />
            <Divider sx={{ my: 2 }} />
            <LoaderSkeleton />
          </Box>
        ) : resultList?.length < 1 ? (
          <Box sx={{ py: 8 }}>
            <Empty description="No submitted results awaiting approval" />
          </Box>
        ) : (
          renderContent()
        )}
      </Paper>

      <Box sx={{ mt: 3, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <Typography variant="body2" color="textSecondary" fontWeight={500}>
          Showing {resultList.length} of {total} pending approvals
        </Typography>
        <AppPagination
          defaultCurrent={1}
          defaultPageSize={pageSize}
          total={total}
          onPageChange={(p) => setPageNum(p)}
        />
      </Box>

      {/* Logic Modals - Functional Integrity Maintained */}
      {openResultLocker && (
        <ResultLock
          open={openResultLocker}
          onClose={() => setOpenResultLocker(false)}
          record={resultSelected}
          onRefreshPage={() => setRefreshPage(true)}
        />
      )}
      {openGenCert && (
        <GenerateCertificate
          open={openGenCert}
          onClose={() => {
            setOpenGenCert(false);
            setRefreshPage(true);
          }}
          title="Generate Certificate"
          report={resultSelected}
        />
      )}
      {openSendCert && (
        <SendCertificate
          open={openSendCert}
          onClose={() => {
            setOpenSendCert(false);
            setRefreshPage(true);
          }}
          title="Send Certificate"
          report={resultSelected}
        />
      )}
      {openPublish && (
        <PublishResult
          open={openPublish}
          title={'Publish Result'}
          onClose={() => {
            setOpenPublish(false);
            setRefreshPage(true);
          }}
          report={resultSelected}
        />
      )}
      {openApproveResult && (
        <ApproveResult
          open={openApproveResult}
          title={'Approve Result'}
          onClose={() => {
            setOpenApproveResult(false);
            setRefreshPage(true);
          }}
          facilityId={resultSelected?.facilityId}
          reportQuarter={resultSelected?.reportQuarter}
          reportYear={resultSelected?.reportYear}
        />
      )}
      {openRejectResult && (
        <RejectResult
          open={openRejectResult}
          title={'Reject Result'}
          onClose={() => {
            setOpenRejectResult(false);
            setRefreshPage(true);
          }}
          report={resultSelected}
          facilityId={resultSelected?.facilityId}
          reportQuarter={resultSelected?.reportQuarter}
          reportYear={resultSelected?.reportYear}
        />
      )}
    </Box>
  );
}
